// "Having access issues?" on the sign-in page: the problems it offers, and the
// email draft each one opens.
//
// It goes to the same mailbox as the intranet's own Help button, so it uses the
// same shape (hd-intranet/src/components/hd/SuggestionBox/config.ts in the
// intranet-web repo), and opens the same way, as a short list to pick from: the
// subject starts with a filterable `[#Tag]`, then
// `Intranet issue`, then where the person was; the body greets the team, names
// the place, and leaves an empty line to write on. The mailbox can then sort
// sign-in problems and intranet problems with the same rules.
//
// What the body adds, because it is what the team needs to help someone who
// cannot get in: the step they were on, the email they typed (only when it looks
// like an email address), the error the page showed them, and the time. Nothing
// else is read: no token, no query string, nothing from the IAP handoff.
//
// No DOM and no imports, so `test/access-help.test.js` pins every rule.

export const ACCESS_HELP_EMAIL = 'help_intranet@hunterdouglas.com';
export const ACCESS_HELP_PLACE = 'Sign-in page';

/**
 * The problems a person can pick, ONLY about getting in: someone on this page is
 * not in the intranet yet, so nothing here names a part of it (the intranet's
 * Help button carries those). Each matches a failure this page can show. The tag
 * goes in the subject for the mailbox to sort on; the label is what the person
 * reads. `Other` is the fallback for an unknown tag.
 */
export const ACCESS_HELP_TAGS = [
  { tag: 'SSO', label: "Microsoft sign-in isn't working" },
  { tag: 'NoCode', label: "I didn't get a code" },
  { tag: 'CodeRejected', label: "My code doesn't work" },
  { tag: 'NotFound', label: "It says I'm not in the employee directory" },
  { tag: 'Other', label: 'Something else' }
];

/** The step names a person would recognise, by the page's own step keys. */
export const ACCESS_HELP_STEPS = {
  choose: 'Choosing how to sign in',
  email: 'Entering my work email',
  code: 'Entering the verification code'
};

/** The longest error text carried into the body. */
export const ACCESS_HELP_MESSAGE_MAX = 300;

/**
 * Mail handlers on Windows and in some browsers truncate or refuse a `mailto:`
 * around 2000 characters, as the intranet's Help button also allows for.
 */
export const ACCESS_HELP_MAILTO_MAX = 1800;

const EMAIL = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;

/** Whitespace and control characters collapsed to single spaces, trimmed. */
function oneLine(value) {
  return String(value ?? '').replace(/[\s\u0000-\u001f\u007f]+/g, ' ').trim();
}

/** One of `ACCESS_HELP_TAGS`, else `Other`. */
export function accessHelpTag(tag) {
  return ACCESS_HELP_TAGS.some(entry => entry.tag === tag) ? tag : 'Other';
}

/** `[#NoCode] Intranet issue · Sign-in page`, the intranet Help button's format. */
export function accessHelpSubject(tag) {
  return `[#${accessHelpTag(tag)}] Intranet issue · ${ACCESS_HELP_PLACE}`;
}

/** The page's address without its query string or fragment, or null. */
export function accessHelpPageUrl(href) {
  try {
    const url = new URL(href);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return `${url.origin}${url.pathname}`;
  } catch {
    return null;
  }
}

/** The typed email when it is one plain address, else null. */
export function accessHelpEmail(value) {
  const email = oneLine(value);
  return email.length <= 254 && EMAIL.test(email) ? email : null;
}

/** The error text, on one line, at most `ACCESS_HELP_MESSAGE_MAX` characters, or null. */
export function accessHelpMessage(value) {
  const text = oneLine(value);
  if (text.length === 0) return null;
  return text.length <= ACCESS_HELP_MESSAGE_MAX ? text : `${text.slice(0, ACCESS_HELP_MESSAGE_MAX - 1).trimEnd()}…`;
}

/** `2026-10-08 15:40 UTC`. */
export function accessHelpTime(date) {
  const iso = date.toISOString();
  return `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`;
}

/**
 * The prefilled body, CRLF line endings as RFC 6068 asks. Lines with nothing to
 * say are left out.
 */
export function accessHelpBody({ step, email, message, pageUrl, now }) {
  const lines = ['Hi intranet team,', '', `Section: ${ACCESS_HELP_PLACE}`];
  if (pageUrl) lines.push(`Page: ${pageUrl}`);
  const stepName = ACCESS_HELP_STEPS[step];
  if (stepName) lines.push(`Step: ${stepName}`);
  if (email) lines.push(`Email I used: ${email}`);
  if (message) lines.push(`Message on screen: ${message}`);
  if (now) lines.push(`Time: ${accessHelpTime(now)}`);
  lines.push('', 'The problem I ran into:', '');
  return lines.join('\r\n');
}

/**
 * The draft's `mailto:`. Every value is `encodeURIComponent`-encoded, so nothing a
 * person typed or a message held can add a recipient or a header. If the URL would
 * be too long the error text goes first, then the whole body.
 */
export function accessHelpMailto({ tag, step, email, message, href, now } = {}) {
  const subjectOnly = `mailto:${ACCESS_HELP_EMAIL}?subject=${encodeURIComponent(accessHelpSubject(tag))}`;
  const facts = {
    step,
    email: accessHelpEmail(email),
    message: accessHelpMessage(message),
    pageUrl: accessHelpPageUrl(href),
    now
  };
  const withBody = (parts) => `${subjectOnly}&body=${encodeURIComponent(accessHelpBody(parts))}`;
  const full = withBody(facts);
  if (full.length <= ACCESS_HELP_MAILTO_MAX) return full;
  const shorter = withBody({ ...facts, message: null });
  return shorter.length <= ACCESS_HELP_MAILTO_MAX ? shorter : subjectOnly;
}
